import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Upload, Loader2, X, FileText, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function SMSCreateDocument() {
  const navigate = useNavigate();
  const [docTypes, setDocTypes] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    doc_type_id: "",
    name: "",
    description: "",
    recommenders: [],
    qc_position: "",
    approver_position: "",
    expected_action_date: "",
    notes: "",
  });
  const [file, setFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [typesRes, posRes] = await Promise.all([
        axios.get(`${API}/sms/document-types`, {
          headers: getHeaders(), withCredentials: true,
        }),
        axios.get(`${API}/sms/positions`, {
          headers: getHeaders(), withCredentials: true,
        }),
      ]);
      setDocTypes(typesRes.data.document_types || []);
      setPositions(posRes.data.positions || []);
    } catch (err) {
      toast.error("Failed to load form data");
    }
    setLoading(false);
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleRecommender = (posId) => {
    setForm(prev => ({
      ...prev,
      recommenders: prev.recommenders.includes(posId)
        ? prev.recommenders.filter(id => id !== posId)
        : [...prev.recommenders, posId],
    }));
  };

  const handleSubmit = async (submitType = "submit") => {
    if (!form.doc_type_id || !form.name.trim()) {
      toast.error("Document type and name are required");
      return;
    }
    if (form.recommenders.length === 0) {
      toast.error("At least one recommender is required");
      return;
    }
    if (!form.qc_position) {
      toast.error("Quality Controller is required");
      return;
    }
    if (!form.approver_position) {
      toast.error("Approver is required");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("req", JSON.stringify({
        ...form,
        expected_action_date: form.expected_action_date
          ? new Date(form.expected_action_date).toISOString()
          : null,
      }));
      if (file) {
        formData.append("file", file);
      }

      await axios.post(`${API}/sms/documents`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });

      toast.success("Document created successfully");
      navigate("/hr-hub/smart-management/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create document");
    }
    setSubmitting(false);
  };

  const selectedType = docTypes.find(dt => dt.id === form.doc_type_id);
  const filteredPositions = positions.filter(p =>
    !searchTerm || p.title?.toLowerCase().includes(searchTerm.toLowerCase())
    || p.post_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 10);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-none"
          onClick={() => navigate("/hr-hub/smart-management/dashboard")}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-800 tracking-wide">
            Create Document
          </h1>
          <p className="text-xs text-slate-500">Fill in the details to create a new document for workflow</p>
        </div>
      </div>

      <Card className="bg-white border-slate-200 p-6">
        <div className="space-y-5">
          {/* Document Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Document Type *</Label>
              <Select value={form.doc_type_id} onValueChange={(v) => updateField("doc_type_id", v)}>
                <SelectTrigger className="rounded-none border-slate-300 h-9 text-xs">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-300 rounded-none">
                  {docTypes.map(dt => (
                    <SelectItem key={dt.id} value={dt.id}>
                      {dt.code} — {dt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedType && (
                <p className="text-[10px] text-slate-400">
                  ID prefix: {selectedType.code} | Workflow: {selectedType.workflow_stages?.join(" → ")}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Expected Action Date *</Label>
              <Input type="date" value={form.expected_action_date}
                onChange={(e) => updateField("expected_action_date", e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                max={maxDate.toISOString().split("T")[0]}
                className="rounded-none border-slate-300 h-9 text-xs"
              />
            </div>
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Document Name *</Label>
            <Input value={form.name} onChange={(e) => updateField("name", e.target.value)}
              placeholder="Enter document name (max 200 characters)" maxLength={200}
              className="rounded-none border-slate-300 h-9 text-xs"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Description</Label>
            <Textarea value={form.description} onChange={(e) => updateField("description", e.target.value)}
              placeholder="Brief description of the document" rows={2}
              className="rounded-none border-slate-300 text-xs"
            />
          </div>

          {/* File upload */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Attachment (.docx, .pdf, .xlsx — max 25MB)</Label>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-9 text-xs rounded-none relative"
                onClick={() => document.getElementById("sms-file-input").click()}
              >
                <Upload className="w-3.5 h-3.5 mr-1" /> Choose File
              </Button>
              <input id="sms-file-input" type="file" className="hidden"
                accept=".docx,.pdf,.xlsx,.doc,.xls,.txt,.csv"
                onChange={(e) => setFile(e.target.files[0])}
              />
              {file && (
                <div className="flex items-center gap-1 text-xs text-slate-600 bg-app px-2 py-1.5 border border-slate-200">
                  <FileText className="w-3.5 h-3.5 text-emerald-500" />
                  <span>{file.name}</span>
                  <Button variant="ghost" size="sm" className="h-4 w-4 p-0 ml-1"
                    onClick={() => setFile(null)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Role selection */}
          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-xs font-semibold text-slate-700 mb-3">Workflow Roles</h3>
            <div className="grid grid-cols-3 gap-4">
              {/* Recommenders */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Recommenders * (multi)</Label>
                <div className="border border-slate-300 max-h-40 overflow-y-auto bg-white">
                  <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search positions..." className="border-0 border-b border-slate-200 rounded-none h-8 text-xs"
                  />
                  {filteredPositions.map(pos => (
                    <label key={pos.id}
                      className={`flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-app ${
                        form.recommenders.includes(pos.id) ? 'bg-emerald-50' : ''
                      }`}
                    >
                      <input type="checkbox" checked={form.recommenders.includes(pos.id)}
                        onChange={() => toggleRecommender(pos.id)}
                        className="w-3 h-3 rounded border-slate-300"
                      />
                      <span className="truncate">{pos.title}</span>
                      <span className="text-[10px] text-slate-400 ml-auto">{pos.post_number}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400">{form.recommenders.length} selected</p>
              </div>

              {/* QC */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Quality Controller *</Label>
                <Select value={form.qc_position} onValueChange={(v) => updateField("qc_position", v)}>
                  <SelectTrigger className="rounded-none border-slate-300 h-9 text-xs">
                    <SelectValue placeholder="Select QC" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-300 rounded-none max-h-60">
                    {positions.map(pos => (
                      <SelectItem key={pos.id} value={pos.id} className="text-xs">
                        {pos.title} ({pos.post_number})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Approver */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Approver *</Label>
                <Select value={form.approver_position} onValueChange={(v) => updateField("approver_position", v)}>
                  <SelectTrigger className="rounded-none border-slate-300 h-9 text-xs">
                    <SelectValue placeholder="Select Approver" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-300 rounded-none max-h-60">
                    {positions.map(pos => (
                      <SelectItem key={pos.id} value={pos.id} className="text-xs">
                        {pos.title} ({pos.post_number})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Additional Notes / Instructions</Label>
            <Textarea value={form.notes} onChange={(e) => updateField("notes", e.target.value)}
              placeholder="Optional notes for the workflow participants" rows={2}
              className="rounded-none border-slate-300 text-xs"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
            <Button onClick={() => handleSubmit("submit")} disabled={submitting}
              className="rounded-none bg-emerald-600 hover:bg-emerald-500"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Submit for Workflow
            </Button>
            <Button variant="outline" disabled={submitting}
              className="rounded-none"
              onClick={() => navigate("/hr-hub/smart-management/dashboard")}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
